import {Actions, showNotification} from 'platform/components';
import {Box, Heading, Hide, HStack, Image, Space, Text, VStack} from 'platform/foundation';

import {InsurerRole, usePostMailMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {handleApiError, useUserFullName, useUserPhoneNumber} from '@dms/shared';

import {InsuranceData} from '../../../hooks/useInsuranceData';

interface BecomeTipperFormProps {
  insuranceData: InsuranceData;
  onDone: VoidFunction;
  onCancel: VoidFunction;
}

export function BecomeTipperForm(props: BecomeTipperFormProps) {
  const [sendMail, {isLoading}] = usePostMailMutation();
  const userName = useUserFullName();
  const phoneNumber = useUserPhoneNumber();

  const handleSubmit = () => {
    if (!userName) {
      return showNotification.error(i18n.t('entity.insurance.errors.noUsername'));
    }
    if (!phoneNumber) {
      return showNotification.error(i18n.t('entity.insurance.errors.noPhone'));
    }
    if (!props.insuranceData?.insurancePerson?.email) {
      return showNotification.error(i18n.t('entity.insurance.errors.noEmail'));
    }

    const tenantName =
      props.insuranceData.tenantInfo?.contactInformation?.companyName ??
      props.insuranceData.tenantInfo?.billingInformation?.[0]?.billingName ??
      '';

    sendMail({
      body: {
        tenantName,
        userName,
        phoneNumber,
        tenantId: props.insuranceData.tenantInfo?.id ?? '',
        email: props.insuranceData.userInfo?.email ?? '',
        roleToApply: InsurerRole.TIPPER,
      },
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.insurance.labels.interestIsSaved')))
      .then(props.onDone)
      .catch(handleApiError);
  };

  return (
    <HStack spacing={6} justify="stretch" align="stretch">
      <Hide onMobile>
        <Box padding={6} backgroundColor="palettes.neutral.20.100">
          <Image width={58} src="assets/images/insurance_handshake.png" />
        </Box>
      </Hide>
      <VStack spacing={6}>
        <Heading size={3}>{i18n.t('entity.insurance.labels.thankYouForYourInterest')}</Heading>
        <Text size="small">{i18n.t('entity.insurance.labels.weWillContactYou')}</Text>
        <Space fillAvailable />
        <Actions
          align="right"
          actions={[
            {
              type: 'button',
              title: i18n.t('general.actions.cancel'),
              onClick: props.onCancel,
              variant: 'secondary',
            },
            {
              type: 'button',
              title: i18n.t('general.actions.confirm'),
              onClick: handleSubmit,
              isLoading,
            },
          ]}
        />
      </VStack>
    </HStack>
  );
}
